import { IssueCaseDoc, IssueSourceType } from '@gooduncles/gu-app-schema';
import { ColDef, ColGroupDef, ICellRendererParams } from 'ag-grid-community';
import 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { FC, useCallback, useMemo, useState } from 'react';
import { loadGridState } from 'src/utils/ag-grid-util';
import { getCategoryLabel, getSubcategoryLabel } from 'src/utils/issue/issue-data-util';
import {
  createCategoryWithTaskCountRenderer,
  createCreatorCellRenderer,
  createImagesCellRenderer,
  createProductsCellRenderer,
  createStatusCellRenderer,
  createStatusDateSummaryCellRenderer,
} from 'src/utils/issue/issue-ag-grid-renderers';
import {
  categoryCellStyle,
  categoryFilterValueFormatter,
  createAmountColumnGroup,
  handleIssuedByCellValueChanged,
  issueCaseStatusValueFormatter,
  issuedByCellEditorParams,
  issuedByValueFormatter,
  productsFilterParams,
  registeredAtValueFormatter,
  storeNameTooltipValueGetter,
  subcategoryValueGetter,
} from 'src/utils/issue/issue-ag-grid-util';
import { buildIssuePanelModalData, IssuePanelModalData, issueCaseStatusBadgeMap } from 'src/utils/issue/issue-util';

import Loading from 'src/components/Loading/Loading';

import IssuePanelModal from '../IssuePanelModal';

interface IssueCaseHistoryTableProps {
  rowData: IssueCaseDoc[];
  isLoading: boolean;
  onGridRefChange?: (gridRef: AgGridReact<IssueCaseDoc> | null) => void;
}

export const ISSUE_CASE_HISTORY_TABLE_ID = 'issueCaseHistoryTable';

const defaultColDef: ColDef = {
  sortable: true,
  resizable: true,
  filter: true,
};

const IssueCaseHistoryTable: FC<IssueCaseHistoryTableProps> = ({ rowData, isLoading, onGridRefChange }) => {
  const [gridRef, setGridRef] = useState<AgGridReact<IssueCaseDoc> | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState<IssuePanelModalData>(null);

  // gridRef 변경 시 부모에게 전달
  const handleGridRef = useCallback(
    (ref: AgGridReact<IssueCaseDoc> | null) => {
      setGridRef(ref);
      onGridRefChange?.(ref);
    },
    [onGridRefChange]
  );

  const handleOpenDetail = useCallback(
    (issueId: string, sourceType: IssueSourceType | null, sourceId: string | null) => {
      const data = buildIssuePanelModalData(issueId, sourceType, sourceId);
      setModalData(data);
      setModalOpen(true);
    },
    []
  );

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setModalData(null);
  }, []);

  /**
   * 카테고리 렌더러 (클릭 시 모달 열기, 태스크 개수 없음)
   */
  const CategoryCellRenderer = useMemo(
    () =>
      createCategoryWithTaskCountRenderer<IssueCaseDoc>({
        onRowClick: (data) => handleOpenDetail(data._id, data.sourceType ?? null, data.sourceId ?? null),
        showTaskCount: false,
      }),
    [handleOpenDetail]
  );

  const CategorySummaryCellRenderer = useCallback(
    (params: ICellRendererParams<IssueCaseDoc>) => {
      const { _id, sourceType, sourceId, category, subcategory } = params.data || {};

      // displayCategoryValueGetter 로직을 직접 구현
      let displayValue = '-';
      if (category) {
        const baseCategory = getCategoryLabel(category);
        if (!subcategory) {
          displayValue = baseCategory;
        } else {
          const subCatStr = getSubcategoryLabel(category, subcategory);
          displayValue = subCatStr ? `${baseCategory} / ${subCatStr}` : baseCategory;
        }
      }

      if (!_id) return displayValue;

      return (
        <span
          style={{ cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => handleOpenDetail(_id, sourceType ?? null, sourceId ?? null)}>
          {displayValue}
        </span>
      );
    },
    [handleOpenDetail]
  );

  const columnDefs = useMemo<(ColDef<IssueCaseDoc> | ColGroupDef<IssueCaseDoc>)[]>(() => {
    return [
      {
        headerName: '카테고리',
        children: [
          {
            headerName: '요약',
            width: 160,
            pinned: 'left',
            columnGroupShow: 'closed',
            cellRenderer: CategorySummaryCellRenderer,
            cellStyle: categoryCellStyle,
          },
          {
            field: 'category',
            headerName: '분류',
            width: 100,
            pinned: 'left',
            columnGroupShow: 'open',
            cellRenderer: CategoryCellRenderer,
            cellStyle: categoryCellStyle,
            filter: 'agSetColumnFilter',
            filterParams: {
              valueFormatter: categoryFilterValueFormatter,
              convertValuesToStrings: true,
            },
          },
          {
            field: 'subcategory',
            headerName: '세부분류',
            width: 100,
            pinned: 'left',
            columnGroupShow: 'open',
            valueGetter: subcategoryValueGetter,
          },
        ],
      },
      {
        headerName: '상태/일시',
        children: [
          {
            headerName: '요약',
            width: 160,
            columnGroupShow: 'closed',
            cellRenderer: createStatusDateSummaryCellRenderer<IssueCaseDoc>(issueCaseStatusBadgeMap),
            filter: false,
            sortable: false,
          },
          {
            field: 'status',
            headerName: '상태',
            width: 120,
            columnGroupShow: 'open',
            cellRenderer: createStatusCellRenderer<IssueCaseDoc>(issueCaseStatusBadgeMap),
            filterParams: {
              valueFormatter: issueCaseStatusValueFormatter,
            },
          },
          {
            field: 'registeredAt',
            headerName: '등록일시',
            width: 140,
            columnGroupShow: 'open',
            valueFormatter: registeredAtValueFormatter,
          },
        ],
      },
      {
        field: 'storeName',
        headerName: '매장명',
        width: 160,
        tooltipValueGetter: storeNameTooltipValueGetter,
      },
      {
        headerName: '이슈 상품',
        width: 200,
        cellRenderer: createProductsCellRenderer<IssueCaseDoc>(),
        filter: 'agSetColumnFilter',
        filterParams: productsFilterParams,
      },
      {
        headerName: '이미지',
        width: 120,
        cellRenderer: createImagesCellRenderer<IssueCaseDoc>(),
        filter: false,
        sortable: false,
      },
      createAmountColumnGroup(),
      {
        field: 'publicNote',
        headerName: '메모(공개)',
        width: 150,
      },
      {
        field: 'privateNote',
        headerName: '메모(비공개)',
        width: 150,
      },
      {
        headerName: '작성자',
        field: 'creatorName',
        width: 100,
        cellRenderer: createCreatorCellRenderer<IssueCaseDoc>(),
      },
      {
        field: 'issuedBy',
        headerName: '출처',
        width: 110,
        editable: true,
        cellEditor: 'agRichSelectCellEditor',
        cellEditorPopup: true,
        cellEditorParams: issuedByCellEditorParams,
        valueFormatter: issuedByValueFormatter,
        onCellValueChanged: handleIssuedByCellValueChanged as ColDef['onCellValueChanged'],
      },
    ];
  }, [CategoryCellRenderer, CategorySummaryCellRenderer]);

  return (
    <>
      <div className='ag-theme-alpine height100'>
        {isLoading && <Loading title='데이터를 불러오고 있습니다.' />}
        <AgGridReact<IssueCaseDoc>
          ref={handleGridRef}
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          getRowId={(params) => params.data._id}
          enableCellTextSelection={true}
          tooltipShowDelay={0}
          animateRows={true}
          onGridReady={() => loadGridState(gridRef, ISSUE_CASE_HISTORY_TABLE_ID)}
        />
      </div>
      {modalData && <IssuePanelModal open={modalOpen} onClose={handleCloseModal} {...modalData} />}
    </>
  );
};

export default IssueCaseHistoryTable;
